// Puzzel ID is a standalone OIDC provider (IdentityServer-based), separate from Vy's Cognito/Entra ID setup.
const PUZZEL_ISSUER = "https://app.puzzel.com/id";

// clientId/idpScheme come from the backend's /app-data endpoint at runtime (see configureAuth.ts)
let runtimeClientId = "";
let runtimeIdpScheme: string | undefined;

export const setPuzzelRuntimeConfig = (config: {
  clientId: string;
  idpScheme?: string;
}): void => {
  runtimeClientId = config.clientId;
  runtimeIdpScheme = config.idpScheme;
};

export const puzzelAuthConfig = {
  issuer: PUZZEL_ISSUER,
  authorizeEndpoint: `${PUZZEL_ISSUER}/connect/authorize`,
  tokenEndpoint: `${PUZZEL_ISSUER}/connect/token`,
  revocationEndpoint: `${PUZZEL_ISSUER}/connect/revocation`,
  scope: "openid profile email contact-centre offline_access",
  get clientId(): string {
    return runtimeClientId;
  },
  redirectUri: `${window.location.origin}/puzzel-callback`,
  // SSO "scheme" value that skips Puzzel ID's own login screen and goes straight to the IdP.
  get idpScheme(): string | undefined {
    return runtimeIdpScheme;
  },
};

// Call-routing/org config needed by the phone widget (e.g. CallOut's /callout request) - used
// to come from the legacy /puzzel/login backend response, now fetched via /app-data instead
// (see configureAuth.ts), same as clientId/idpScheme above.
export type PuzzelOrgConfig = {
  ciqType: string;
  phoneNumberPrefix: string;
  functionalNumberPrefix: string;
  countryCode: string;
  maxAttempts: number;
  maxOneRequestForPreferredAgent: boolean;
  preferredAgentWaitTimeSeconds: number;
  queueKey: string;
  reservedAgentWaitTimeSeconds: number;
  secondsBetweenAttempts: number;
};

const emptyPuzzelOrgConfig: PuzzelOrgConfig = {
  ciqType: "",
  phoneNumberPrefix: "",
  functionalNumberPrefix: "",
  countryCode: "",
  maxAttempts: 0,
  maxOneRequestForPreferredAgent: false,
  preferredAgentWaitTimeSeconds: 0,
  queueKey: "",
  reservedAgentWaitTimeSeconds: 0,
  secondsBetweenAttempts: 0,
};

let runtimeOrgConfig: PuzzelOrgConfig = emptyPuzzelOrgConfig;

export const setPuzzelOrgConfig = (config: PuzzelOrgConfig): void => {
  runtimeOrgConfig = config;
};

export const getPuzzelOrgConfig = (): PuzzelOrgConfig => runtimeOrgConfig;

// Controls the rollout of the new Puzzel ID OIDC login flow vs. the legacy silent
// backend-mediated login. Read from a React Query-backed feature flag (useFeatureFlag), but
// also needed inside puzzelHttpClient.ts's axios interceptor, which runs outside React.
export const PUZZEL_OIDC_LOGIN_FLAG = "puzzelOidcLogin";

let isOidcEnabled = false;

export const setPuzzelOidcEnabled = (enabled: boolean): void => {
  isOidcEnabled = enabled;
};

export const isPuzzelOidcEnabled = (): boolean => isOidcEnabled;
